import { type GroupChannel } from "@sendbird/chat/groupChannel";
import { compareAsc, parseISO } from "date-fns";

import { type ShiftsByFacility } from "../api/useGetUpcomingShiftsGrouped";
import { type ChatChannel, type ChatChannelMetadata } from "../types";

/**
 * Extracts metadata fields from a Sendbird GroupChannel's `cachedMetaData`
 * into a typed `ChatChannelMetadata` object.
 */
function extractMetadata(cachedMetaData: Record<string, string>): ChatChannelMetadata {
  const {
    facilityId,
    facilityName,
    facilityType,
    hcpName,
    lastBooked,
    placementCandidateAccessId,
    placementId,
    placementTitle,
  } = cachedMetaData as unknown as ChatChannelMetadata;

  return {
    facilityId,
    facilityName,
    facilityType,
    hcpName,
    lastBooked,
    placementCandidateAccessId,
    placementId,
    placementTitle,
  };
}

/**
 * Converts a raw Sendbird GroupChannel into a plain ChatChannel object.
 * Returns a new object — the original GroupChannel is never mutated.
 */
function toEnrichedChannel(raw: GroupChannel): ChatChannel {
  return {
    url: raw.url,
    name: raw.name,
    customType: raw.customType,
    createdAt: raw.createdAt,
    unreadMessageCount: raw.unreadMessageCount,
    lastMessage: raw.lastMessage,
    shift: undefined,
    metadata: extractMetadata(raw.cachedMetaData as Record<string, string>),
  };
}

/**
 * Maps upcoming shifts to channels by extracting facility ID from the channel URL.
 * Channel URLs follow the pattern `{facilityId}_{workerId}`.
 * Returns new channel objects — inputs are not mutated.
 */
export function enrichChannelsWithShifts(
  channels: ChatChannel[],
  shiftsByFacility: ShiftsByFacility | undefined
): ChatChannel[] {
  if (!shiftsByFacility) {
    return channels;
  }

  return channels.map((channel) => {
    const facilityId = channel.url?.split("_")[0];
    const shift = shiftsByFacility[facilityId];
    if (shift) {
      return { ...channel, shift };
    }

    return channel;
  });
}

/**
 * Sorts channels by priority:
 * 1. Unread channels first, sorted by last message time (newest first)
 * 2. Channels with upcoming shifts, sorted by shift start (earliest first)
 * 3. Everything else by last message time (newest first)
 */
export function sortChatChannels(channels: ChatChannel[]): ChatChannel[] {
  return channels.toSorted((a: ChatChannel, b: ChatChannel) => {
    const aTime = a.lastMessage ? a.lastMessage.createdAt : a.createdAt;
    const bTime = b.lastMessage ? b.lastMessage.createdAt : b.createdAt;

    if (a.unreadMessageCount && b.unreadMessageCount) {
      return bTime - aTime;
    }

    if (a.unreadMessageCount) {
      return -1;
    }
    if (b.unreadMessageCount) {
      return 1;
    }

    if (a.shift && b.shift) {
      return compareAsc(parseISO(a.shift.start), parseISO(b.shift.start));
    }
    if (a.shift) {
      return -1;
    }
    if (b.shift) {
      return 1;
    }

    return bTime - aTime;
  });
}

/**
 * Enriches raw Sendbird GroupChannels with metadata and shift data, then sorts.
 * Returns new plain objects — the original GroupChannel instances are never mutated.
 */
export function enrichAndSortChannels(
  rawChannels: GroupChannel[],
  shiftsByFacility: ShiftsByFacility | undefined
): ChatChannel[] {
  const channels = rawChannels.map(toEnrichedChannel);
  const withShifts = enrichChannelsWithShifts(channels, shiftsByFacility);
  return sortChatChannels(withShifts);
}
